import { PieChart } from "lucide-react";
import { Card } from "@components/ui/card";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@components/ui/table";
import { useState } from "react";

export function Summary({
  allPlayerStats,
  loading,
  error,
  startDate,
  endDate,
}: {
  allPlayerStats: any;
  loading: boolean;
  error: string | null;
  startDate?: Date;
  endDate?: Date;
}) {
  const [sortKey, setSortKey] = useState<string>("signupDate");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [page, setPage] = useState(1);
  const pageSize = 10;

  // Defensive: support both array and object with players array
  const playersArray = Array.isArray(allPlayerStats)
    ? allPlayerStats
    : allPlayerStats &&
      typeof allPlayerStats === "object" &&
      "players" in allPlayerStats &&
      Array.isArray((allPlayerStats as any).players)
    ? (allPlayerStats as any).players
    : [];

  const columns = [
    { key: "playerId", label: "Player ID" },
    { key: "countryName", label: "Country" },
    { key: "device", label: "Device" },
    { key: "signupDate", label: "Signup Date" },
    { key: "gamesPlayed", label: "Games Played" },
    { key: "questionsPlayed", label: "Questions Played" },
    { key: "wonGames", label: "Games Won" },
    { key: "prizesWon", label: "Prizes Won" },
  ];
  function getValue(row: any, key: string) {
    if (key === "signupDate")
      return row.signupDate
        ? new Date(row.signupDate).toLocaleDateString()
        : "-";
    return row[key] ?? "-";
  }
  const sortedRows = [...playersArray].sort((a, b) => {
    let aVal = getValue(a, sortKey);
    let bVal = getValue(b, sortKey);
    // If sorting by signupDate, compare as Date objects
    if (sortKey === "signupDate") {
      const aDate = a.signupDate ? new Date(a.signupDate) : null;
      const bDate = b.signupDate ? new Date(b.signupDate) : null;
      if (!aDate && !bDate) return 0;
      if (!aDate) return sortDir === "asc" ? -1 : 1;
      if (!bDate) return sortDir === "asc" ? 1 : -1;
      if (aDate.getTime() === bDate.getTime()) return 0;
      return sortDir === "asc"
        ? aDate.getTime() - bDate.getTime()
        : bDate.getTime() - aDate.getTime();
    }
    // Fallback: string/number comparison
    if (aVal === bVal) return 0;
    if (sortDir === "asc") return aVal > bVal ? 1 : -1;
    return aVal < bVal ? 1 : -1;
  });
  const totalPages = Math.ceil(sortedRows.length / pageSize);
  const pagedRows = sortedRows.slice((page - 1) * pageSize, page * pageSize);
  function handleSort(key: string) {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  }
  return (
    <Card className="hover:shadow-lg transition-shadow duration-200 overflow-hidden p-6 border border-gray-800">
      <h2 className="text-lg font-semibold font-heading mb-4 flex items-center text-superone-charcoal">
        <PieChart className="mr-2 h-5 w-5 text-pink-500" />
        Summary
      </h2>
      {loading ? (
        <div className="text-gray-400">Loading...</div>
      ) : error ? (
        <div className="text-red-500">{error}</div>
      ) : (
        <div className="overflow-x-auto">
          <div className="relative">
            <Table className="rounded-md overflow-hidden border border-gray-200">
              <TableHeader>
                <TableRow className="bg-[#0F87F2]">
                  {columns.map((col, idx) => (
                    <TableHead
                      key={col.key}
                      className="select-none text-white text-base cursor-pointer bg-[#0F87F2] whitespace-nowrap"
                      onClick={() => handleSort(col.key)}
                    >
                      <span className="flex items-center gap-1">
                        {col.label}
                        {sortKey === col.key && (
                          <span className="ml-1">
                            {sortDir === "asc" ? (
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 12 12"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                aria-label="sorted ascending"
                              >
                                <path
                                  d="M6 3L10 8H2L6 3Z"
                                  fill="currentColor"
                                />
                              </svg>
                            ) : (
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 12 12"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                aria-label="sorted descending"
                              >
                                <path
                                  d="M6 9L2 4H10L6 9Z"
                                  fill="currentColor"
                                />
                              </svg>
                            )}
                          </span>
                        )}
                      </span>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagedRows.map((p, i) => (
                  <TableRow key={p.playerId ?? i}>
                    {columns.map((col, idx) => (
                      <TableCell
                        key={col.key}
                        className={
                          col.label.length < 14
                            ? "whitespace-nowrap"
                            : undefined
                        }
                      >
                        {col.key === "playerId"
                          ? getValue(p, col.key)
                          : getValue(p, col.key)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="h-1 bg-[#0F87F2] w-full z-10" />
          </div>
          <div className="flex items-center justify-between mt-4">
            <button
              className={`px-3 py-1 rounded text-sm font-medium ${
                page === 1
                  ? "bg-gray-100 text-gray-400 cursor-default"
                  : "bg-[#0F87F2] text-white hover:bg-[#0C6DC2] cursor-pointer"
              }`}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </button>
            <span className="text-sm">
              Page {page} of {totalPages}
            </span>
            <button
              className={`px-3 py-1 rounded text-sm font-medium ${
                page === totalPages
                  ? "bg-gray-100 text-gray-400 cursor-default"
                  : "bg-[#0F87F2] text-white hover:bg-[#0C6DC2] cursor-pointer"
              }`}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}
